document.addEventListener("DOMContentLoaded", () => {
  const experience = document.querySelector("[data-dd-first-drop]");
  if (!experience || experience.dataset.firstDropBound === "true") return;

  experience.dataset.firstDropBound = "true";

  const loader = document.querySelector("[data-dd-loader]");
  const authenticated = experience.dataset.authenticated === "true";
  const customerOrders = Number.parseInt(
    experience.dataset.customerOrders || "0",
    10,
  );
  const dismissible = experience.dataset.dismissible !== "false";
  const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
  const lowPower =
    reducedMotion.matches ||
    window.matchMedia("(max-width: 600px)").matches ||
    (navigator.hardwareConcurrency && navigator.hardwareConcurrency <= 4);
  const rewardKeys = ["percent_15", "free_towel", "free_disc"];
  const storage = {
    complete: "dd:first-drop:completed:v2",
    dismissed: "dd:first-drop:dismissed:v2",
    authPending: "dd:first-drop:auth-pending:v2",
    selection: "dd:first-drop:selection:v2",
  };

  const stages = new Map(
    [...experience.querySelectorAll("[data-first-drop-stage]")].map((stage) => [
      stage.dataset.firstDropStage,
      stage,
    ]),
  );
  const closeButton = experience.querySelector("[data-first-drop-close]");
  const ineligibleCloseButton = experience.querySelector(
    "[data-first-drop-ineligible-close]",
  );
  const assignButton = experience.querySelector("[data-first-drop-assign]");
  const authLink = experience.querySelector("[data-first-drop-auth-link]");
  const liveRegion = experience.querySelector("[data-first-drop-live]");
  const instruction = experience.querySelector("[data-first-drop-instruction]");
  const boxTitle = experience.querySelector("[data-first-drop-box-title]");
  const boxStage = stages.get("box");
  const cutSurface = experience.querySelector("[data-first-drop-cut-surface]");
  const reducedOpen = experience.querySelector("[data-first-drop-reduced-open]");
  const ambientParticles = experience.querySelector(
    "[data-first-drop-particles]",
  );
  const burst = experience.querySelector("[data-first-drop-burst]");
  const claimButton = experience.querySelector("[data-first-drop-claim]");
  const savedCopy = experience.querySelector("[data-first-drop-saved]");
  const tokenTrack = experience.querySelector("[data-first-drop-tokens]");
  const rewardTokens = [
    ...experience.querySelectorAll("[data-first-drop-token]"),
  ];

  const rewards = {
    percent_15: {
      key: "percent_15",
      code: experience.dataset.codePercent,
    },
    free_towel: {
      key: "free_towel",
      code: experience.dataset.codeTowel,
    },
    free_disc: {
      key: "free_disc",
      code: experience.dataset.codeDisc,
    },
  };

  let currentStage = "box";
  let selectedReward = null;
  let dragging = false;
  let cutProgress = 0;
  let completedCut = false;
  let previousFocus = null;
  let booted = false;
  let lastSparkAt = 0;
  let sequenceId = 0;
  const timers = new Set();
  const slots = new Map(rewardTokens.map((token, index) => [token, index]));

  if (closeButton && !dismissible) closeButton.hidden = true;

  const readStorage = (store, key) => {
    try {
      return store.getItem(key);
    } catch (_) {
      return null;
    }
  };

  const writeStorage = (store, key, value) => {
    try {
      store.setItem(key, value);
    } catch (_) {}
  };

  const removeStorage = (store, key) => {
    try {
      store.removeItem(key);
    } catch (_) {}
  };

  const safeStorefrontPath = (value, fallback = "/") => {
    if (
      typeof value !== "string" ||
      !value.startsWith("/") ||
      value.startsWith("//")
    ) {
      return fallback;
    }
    try {
      const url = new URL(value, window.location.origin);
      if (url.origin !== window.location.origin) return fallback;
      return `${url.pathname}${url.search}${url.hash}`;
    } catch (_) {
      return fallback;
    }
  };

  const storedSelection = () => {
    const key = readStorage(localStorage, storage.selection);
    return rewardKeys.includes(key) ? rewards[key] : null;
  };

  const schedule = (callback, delay) => {
    const timer = window.setTimeout(() => {
      timers.delete(timer);
      callback();
    }, delay);
    timers.add(timer);
    return timer;
  };

  const wait = (delay) =>
    new Promise((resolve) => {
      schedule(resolve, delay);
    });

  const clearTimers = () => {
    sequenceId += 1;
    timers.forEach((timer) => window.clearTimeout(timer));
    timers.clear();
    rewardTokens.forEach((token) =>
      token.getAnimations().forEach((animation) => animation.cancel()),
    );
  };

  const announce = (message) => {
    if (!message || !liveRegion) return;
    liveRegion.textContent = "";
    window.requestAnimationFrame(() => {
      liveRegion.textContent = message;
    });
  };

  const focusable = () =>
    [
      ...experience.querySelectorAll(
        "button:not([hidden]):not([disabled]), input:not([hidden]):not([disabled]), a[href]:not([hidden]), [tabindex='0']",
      ),
    ].filter((element) => element.offsetParent !== null);

  const showStage = (name) => {
    stages.forEach((stage, stageName) => {
      const active = stageName === name;
      stage.hidden = !active;
      stage.setAttribute("aria-hidden", String(!active));
      stage.classList.toggle("is-entering", active);
    });
    currentStage = name;
  };

  const openExperience = (stageName) => {
    if (!experience.hidden && experience.classList.contains("is-visible")) {
      showStage(stageName);
      return;
    }
    previousFocus = document.activeElement;
    experience.hidden = false;
    experience.setAttribute("aria-hidden", "false");
    document.body.classList.add("dd-first-drop-active");
    showStage(stageName);
    window.requestAnimationFrame(() => {
      experience.classList.add("is-visible");
      schedule(() => focusable()[0]?.focus({ preventScroll: true }), 120);
    });
  };

  const closeExperience = ({ remember = true } = {}) => {
    if (experience.hidden) return;
    clearTimers();
    dragging = false;
    experience.classList.add("is-closing");
    experience.setAttribute("aria-hidden", "true");
    document.body.classList.remove("dd-first-drop-active");
    if (remember) writeStorage(sessionStorage, storage.dismissed, "true");
    schedule(() => {
      experience.classList.remove("is-visible", "is-closing");
      experience.hidden = true;
      previousFocus?.focus?.({ preventScroll: true });
    }, reducedMotion.matches ? 10 : 620);
  };

  const createAmbientParticles = () => {
    if (!ambientParticles || ambientParticles.childElementCount) return;
    const count = lowPower ? 9 : 22;
    const fragment = document.createDocumentFragment();
    for (let index = 0; index < count; index += 1) {
      const particle = document.createElement("span");
      particle.className = "dd-first-drop__particle";
      particle.style.setProperty("--particle-x", `${Math.random() * 100}%`);
      particle.style.setProperty(
        "--particle-y",
        `${25 + Math.random() * 75}%`,
      );
      particle.style.setProperty(
        "--particle-size",
        `${1 + Math.random() * 2.2}px`,
      );
      particle.style.setProperty(
        "--particle-duration",
        `${5.5 + Math.random() * 7}s`,
      );
      particle.style.setProperty(
        "--particle-delay",
        `${Math.random() * -9}s`,
      );
      particle.style.setProperty(
        "--particle-drift",
        `${-32 + Math.random() * 64}px`,
      );
      fragment.append(particle);
    }
    ambientParticles.append(fragment);
  };

  const createBurst = (amount = lowPower ? 12 : 28) => {
    if (!burst) return;
    const fragment = document.createDocumentFragment();
    for (let index = 0; index < amount; index += 1) {
      const angle = (Math.PI * 2 * index) / amount + Math.random() * 0.24;
      const distance = 70 + Math.random() * 190;
      const particle = document.createElement("span");
      particle.className =
        "dd-first-drop__particle dd-first-drop__particle--burst";
      particle.style.setProperty("--burst-x", `${Math.cos(angle) * distance}px`);
      particle.style.setProperty("--burst-y", `${Math.sin(angle) * distance}px`);
      particle.style.setProperty("--burst-r", `${Math.random() * 360}deg`);
      particle.style.setProperty(
        "--particle-size",
        `${2 + Math.random() * 5}px`,
      );
      particle.style.setProperty(
        "--particle-duration",
        `${580 + Math.random() * 520}ms`,
      );
      fragment.append(particle);
    }
    burst.append(fragment);
    schedule(() => burst.replaceChildren(), 1200);
  };

  const createDragSpark = (progress) => {
    if (!burst || lowPower) return;
    const now = performance.now();
    if (now - lastSparkAt < 42) return;
    lastSparkAt = now;
    const spark = document.createElement("span");
    spark.className = "dd-first-drop__drag-spark";
    spark.style.setProperty("--spark-progress", progress);
    spark.style.setProperty("--spark-x", `${-18 + Math.random() * 36}px`);
    spark.style.setProperty("--spark-y", `${-22 + Math.random() * 44}px`);
    burst.append(spark);
    schedule(() => spark.remove(), 440);
  };

  const setCutProgress = (value) => {
    cutProgress = Math.max(0, Math.min(1, value));
    experience.style.setProperty("--cut-progress", cutProgress.toFixed(4));
  };

  const setInstruction = (copy, title = null) => {
    if (instruction && copy) instruction.textContent = copy;
    if (boxTitle && title) boxTitle.textContent = title;
    announce(copy);
  };

  const cardSpacing = () => {
    if (!tokenTrack) return 200;
    return (
      Number.parseFloat(
        window
          .getComputedStyle(tokenTrack)
          .getPropertyValue("--fd-card-spacing"),
      ) || 200
    );
  };

  const slotX = (slot) => (slot - 1) * cardSpacing();

  const cardTransform = (slot, lift = 0, scale = 1, rotation = 0) =>
    `translate(-50%, -50%) translate3d(${slotX(slot)}px, ${lift}px, 0) rotate(${rotation}deg) scale(${scale})`;

  const placeCards = ({ visible = true } = {}) => {
    rewardTokens.forEach((token) => {
      token.style.transform = cardTransform(slots.get(token));
      token.style.opacity = visible ? "1" : "0";
      token.style.filter = "none";
      token.style.zIndex = "7";
    });
  };

  const animateCard = (token, keyframes, options) => {
    if (reducedMotion.matches || typeof token.animate !== "function") {
      const finalFrame = keyframes.at(-1);
      Object.assign(token.style, finalFrame);
      return Promise.resolve();
    }
    const animation = token.animate(keyframes, options);
    return animation.finished.catch(() => undefined);
  };

  const presentCards = async (runId) => {
    boxStage.classList.add("is-presenting");
    boxStage.dataset.phase = "presenting";
    setInstruction(experience.dataset.copyMeetRewards);
    placeCards({ visible: true });

    await Promise.all(
      rewardTokens.map((token, index) =>
        animateCard(
          token,
          [
            {
              opacity: "0",
              transform: `translate(-50%, -50%) translate3d(0, 110px, 0) rotate(${(index - 1) * 9}deg) scale(.56)`,
            },
            {
              opacity: "1",
              offset: 0.72,
              transform: cardTransform(
                slots.get(token),
                index === 1 ? -18 : -9,
                1.04,
                (index - 1) * -2,
              ),
            },
            {
              opacity: "1",
              transform: cardTransform(slots.get(token)),
            },
          ],
          {
            duration: 920,
            delay: index * 130,
            easing: "cubic-bezier(.16,1,.3,1)",
            fill: "both",
          },
        ),
      ),
    );
    if (runId !== sequenceId) return false;
    placeCards({ visible: true });
    return true;
  };

  const flipCards = async (runId) => {
    boxStage.classList.remove("is-presenting");
    boxStage.classList.add("is-flipping");
    boxStage.dataset.phase = "flipping";
    setInstruction(experience.dataset.copyCardsTurning);
    rewardTokens.forEach((token, index) => {
      schedule(() => token.classList.add("is-face-down"), index * 105);
    });
    await wait(reducedMotion.matches ? 30 : 950);
    return runId === sequenceId;
  };

  const swapSlots = async (leftSlot, rightSlot, runId, swapIndex) => {
    const first = rewardTokens.find((token) => slots.get(token) === leftSlot);
    const second = rewardTokens.find((token) => slots.get(token) === rightSlot);
    if (!first || !second || runId !== sequenceId) return false;

    const duration = reducedMotion.matches ? 1 : 720;
    const firstLift = swapIndex % 2 === 0 ? -46 : 46;
    const secondLift = firstLift * -1;
    const midpoint = (slotX(leftSlot) + slotX(rightSlot)) / 2;
    const firstStart = cardTransform(leftSlot);
    const secondStart = cardTransform(rightSlot);
    const firstEnd = cardTransform(rightSlot);
    const secondEnd = cardTransform(leftSlot);

    first.style.zIndex = "9";
    second.style.zIndex = "8";
    await Promise.all([
      animateCard(
        first,
        [
          { transform: firstStart },
          {
            transform: `translate(-50%, -50%) translate3d(${midpoint}px, ${firstLift}px, 0) rotate(${firstLift < 0 ? -3 : 3}deg) scale(1.035)`,
            offset: 0.5,
          },
          { transform: firstEnd },
        ],
        { duration, easing: "cubic-bezier(.45,0,.2,1)", fill: "both" },
      ),
      animateCard(
        second,
        [
          { transform: secondStart },
          {
            transform: `translate(-50%, -50%) translate3d(${midpoint}px, ${secondLift}px, 0) rotate(${secondLift < 0 ? -3 : 3}deg) scale(.985)`,
            offset: 0.5,
          },
          { transform: secondEnd },
        ],
        { duration, easing: "cubic-bezier(.45,0,.2,1)", fill: "both" },
      ),
    ]);

    if (runId !== sequenceId) return false;
    slots.set(first, rightSlot);
    slots.set(second, leftSlot);
    first.style.transform = firstEnd;
    second.style.transform = secondEnd;
    first.style.zIndex = "7";
    second.style.zIndex = "7";
    return true;
  };

  const shuffleCards = async (runId) => {
    boxStage.classList.remove("is-flipping");
    boxStage.classList.add("is-shuffling");
    boxStage.dataset.phase = "shuffling";
    setInstruction(experience.dataset.copyTrackReward);

    const swaps = [
      [0, 1],
      [1, 2],
      [0, 2],
      [0, 1],
    ];
    for (let index = 0; index < swaps.length; index += 1) {
      const completed = await swapSlots(
        swaps[index][0],
        swaps[index][1],
        runId,
        index,
      );
      if (!completed) return false;
      await wait(reducedMotion.matches ? 1 : 210);
      if (runId !== sequenceId) return false;
    }
    return true;
  };

  const enableChoice = () => {
    boxStage.classList.remove("is-shuffling");
    boxStage.classList.add("is-choosing");
    boxStage.dataset.phase = "choosing";
    rewardTokens.forEach((token) => {
      token.disabled = false;
      token.setAttribute("aria-label", experience.dataset.copyChooseCard);
    });
    setInstruction(
      experience.dataset.copyChooseReward,
      experience.dataset.copyChooseTitle,
    );
  };

  const runCardSequence = async () => {
    const runId = ++sequenceId;
    const presented = await presentCards(runId);
    if (!presented) return;
    await wait(reducedMotion.matches ? 25 : 1650);
    if (runId !== sequenceId) return;
    const flipped = await flipCards(runId);
    if (!flipped) return;
    const shuffled = await shuffleCards(runId);
    if (!shuffled) return;
    enableChoice();
  };

  const selectReward = (token, { resume = false } = {}) => {
    const key = token?.dataset.firstDropToken;
    if (!rewardKeys.includes(key) || !boxStage) return;

    clearTimers();
    selectedReward = rewards[key];
    writeStorage(localStorage, storage.selection, key);
    experience.dataset.winner = key;
    boxStage.dataset.winner = key;
    boxStage.dataset.phase = "selected";
    boxStage.classList.remove(
      "is-presenting",
      "is-flipping",
      "is-shuffling",
      "is-choosing",
    );
    boxStage.classList.add("is-selected");
    rewardTokens.forEach((rewardToken) => {
      rewardToken.disabled = true;
      const chosen = rewardToken === token;
      rewardToken.classList.toggle("is-selected-card", chosen);
      if (chosen) {
        rewardToken.classList.remove("is-face-down");
        rewardToken.style.opacity = "1";
        rewardToken.style.filter = "none";
        rewardToken.style.zIndex = "10";
        rewardToken.style.transform = cardTransform(1, -86, 1.36);
      } else {
        rewardToken.style.opacity = "0.07";
        rewardToken.style.filter = "blur(6px)";
        rewardToken.style.zIndex = "2";
      }
    });
    setInstruction(
      experience.dataset.copyRewardChosen,
      experience.dataset.copyChosenTitle,
    );
    createBurst(lowPower ? 9 : 18);

    const showResult = () => {
      boxStage.classList.add("is-result");
      if (savedCopy) {
        savedCopy.textContent = authenticated
          ? experience.dataset.copyReady
          : experience.dataset.copyNeedsAccount;
      }
      announce(
        authenticated
          ? experience.dataset.copyReady
          : experience.dataset.copyNeedsAccount,
      );
      claimButton?.focus({ preventScroll: true });
    };
    if (resume || reducedMotion.matches) showResult();
    else schedule(showResult, 760);
  };

  const completeCut = () => {
    if (completedCut || !boxStage) return;
    completedCut = true;
    dragging = false;
    setCutProgress(1);
    cutSurface?.classList.remove("is-dragging");
    cutSurface?.setAttribute("aria-disabled", "true");
    boxStage.classList.add("is-opening");
    boxStage.dataset.phase = "opening";
    setInstruction(experience.dataset.copyCutComplete);
    createBurst();
    navigator.vibrate?.(35);
    experience.dispatchEvent(
      new CustomEvent("dd:first-drop-impact", {
        detail: { soundReady: true },
      }),
    );
    schedule(runCardSequence, reducedMotion.matches ? 30 : 690);
  };

  const resetCut = () => {
    cutSurface?.classList.add("is-resetting");
    setCutProgress(0);
    schedule(() => cutSurface?.classList.remove("is-resetting"), 420);
    setInstruction(experience.dataset.copyStartLeft);
  };

  const resetBox = () => {
    clearTimers();
    selectedReward = null;
    completedCut = false;
    dragging = false;
    slots.clear();
    rewardTokens.forEach((token, index) => {
      slots.set(token, index);
      token.disabled = true;
      token.classList.remove("is-face-down", "is-selected-card");
      token.style.removeProperty("transform");
      token.style.removeProperty("opacity");
      token.style.removeProperty("filter");
      token.style.removeProperty("z-index");
    });
    boxStage.classList.remove(
      "is-opening",
      "is-presenting",
      "is-flipping",
      "is-shuffling",
      "is-choosing",
      "is-selected",
      "is-result",
    );
    boxStage.dataset.phase = "sealed";
    delete boxStage.dataset.winner;
    delete experience.dataset.winner;
    cutSurface?.removeAttribute("aria-disabled");
    setCutProgress(0);
    if (boxTitle) boxTitle.textContent = experience.dataset.copyBoxTitle;
    if (instruction) instruction.textContent = experience.dataset.copyStartLeft;
  };

  const showFreshBox = () => {
    resetBox();
    openExperience("box");
    schedule(() => cutSurface?.focus({ preventScroll: true }), 180);
  };

  const resumeSelection = (selection) => {
    if (!selection) return showFreshBox();
    resetBox();
    completedCut = true;
    setCutProgress(1);
    boxStage.classList.add("is-opening");
    placeCards({ visible: true });
    const token = rewardTokens.find(
      (item) => item.dataset.firstDropToken === selection.key,
    );
    selectReward(token, { resume: true });
    openExperience("box");
  };

  const showClaimGate = () => {
    const selection = selectedReward || storedSelection();
    if (!selection) {
      showFreshBox();
      return;
    }
    selectedReward = selection;
    experience.dataset.winner = selection.key;
    openExperience("entry");
  };

  const showIneligible = () => {
    openExperience("ineligible");
    announce(experience.dataset.copyIneligible);
  };

  const applyReward = () => {
    const selection = selectedReward || storedSelection();
    if (!selection) {
      showFreshBox();
      return;
    }
    if (!authenticated) {
      showClaimGate();
      return;
    }
    if (customerOrders > 0) {
      showIneligible();
      return;
    }
    if (!selection.code) {
      announce(experience.dataset.copyDiscountUnavailable);
      return;
    }

    writeStorage(localStorage, storage.complete, "true");
    removeStorage(sessionStorage, storage.authPending);
    const builderPath = safeStorefrontPath(
      experience.dataset.builderPath,
      "/#flight-profile",
    );
    const applyPath = `/discount/${encodeURIComponent(selection.code)}`;
    window.location.assign(
      `${applyPath}?redirect=${encodeURIComponent(builderPath)}`,
    );
  };

  const continueParameter = new URL(window.location.href).searchParams.get(
    "first_drop",
  );
  const forcePreview = continueParameter === "preview";
  const authPending =
    readStorage(sessionStorage, storage.authPending) === "true" ||
    continueParameter === "continue";

  const cleanContinueParameter = () => {
    if (!continueParameter || !window.history.replaceState) return;
    const url = new URL(window.location.href);
    url.searchParams.delete("first_drop");
    window.history.replaceState({}, "", `${url.pathname}${url.search}${url.hash}`);
  };

  const boot = () => {
    if (booted) return;
    booted = true;
    createAmbientParticles();
    const selection = storedSelection();

    if (forcePreview) {
      cleanContinueParameter();
      showFreshBox();
      return;
    }
    if (authenticated && customerOrders > 0) {
      cleanContinueParameter();
      if (authPending && selection) showIneligible();
      return;
    }
    if (authPending && selection) {
      cleanContinueParameter();
      selectedReward = selection;
      showClaimGate();
      return;
    }
    if (readStorage(localStorage, storage.complete) === "true") return;
    if (readStorage(sessionStorage, storage.dismissed) === "true") return;
    if (selection) resumeSelection(selection);
    else showFreshBox();
  };

  authLink?.addEventListener("click", () => {
    writeStorage(sessionStorage, storage.authPending, "true");
  });

  assignButton?.addEventListener("click", applyReward);
  claimButton?.addEventListener("click", applyReward);
  closeButton?.addEventListener("click", () => closeExperience());
  ineligibleCloseButton?.addEventListener("click", () => closeExperience());

  rewardTokens.forEach((token) => {
    token.addEventListener("click", () => {
      if (boxStage.dataset.phase !== "choosing") return;
      selectReward(token);
    });
  });

  cutSurface?.addEventListener("pointerdown", (event) => {
    if (completedCut || boxStage.dataset.phase !== "sealed") return;
    const rect = cutSurface.getBoundingClientRect();
    const start = (event.clientX - rect.left) / rect.width;
    if (start > 0.34) {
      setInstruction(experience.dataset.copyStartLeft);
      return;
    }
    dragging = true;
    cutSurface.setPointerCapture?.(event.pointerId);
    cutSurface.classList.add("is-dragging");
    setInstruction(experience.dataset.copyKeepDragging);
    event.preventDefault();
  });

  cutSurface?.addEventListener("pointermove", (event) => {
    if (!dragging || completedCut) return;
    const rect = cutSurface.getBoundingClientRect();
    const raw =
      (event.clientX - rect.left - rect.width * 0.1) / (rect.width * 0.8);
    const resisted = Math.max(0, Math.min(1, raw)) ** 1.045;
    setCutProgress(resisted);
    createDragSpark(resisted);
    event.preventDefault();
    if (resisted >= 0.92) completeCut();
  });

  const endPointer = (event) => {
    if (!dragging) return;
    dragging = false;
    cutSurface?.releasePointerCapture?.(event.pointerId);
    cutSurface?.classList.remove("is-dragging");
    if (!completedCut) resetCut();
  };
  cutSurface?.addEventListener("pointerup", endPointer);
  cutSurface?.addEventListener("pointercancel", endPointer);

  cutSurface?.addEventListener("keydown", (event) => {
    if (completedCut) return;
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      completeCut();
    }
  });

  reducedOpen?.addEventListener("click", completeCut);

  experience.addEventListener("keydown", (event) => {
    if (event.key === "Escape" && dismissible) {
      event.preventDefault();
      closeExperience();
      return;
    }
    if (event.key !== "Tab") return;
    const items = focusable();
    if (!items.length) return;
    const first = items[0];
    const last = items.at(-1);
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  });

  if (!loader || loader.classList.contains("dd-loader--hide")) {
    window.requestAnimationFrame(boot);
  } else {
    document.addEventListener("dd:loader-complete", boot, { once: true });
    schedule(boot, 4200);
  }
});
